import React, { useState, useEffect, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ReactResizeDetector from 'react-resize-detector';
import { Box, Typography, Link } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import {
  paginateArr,
  sortArrByKey,
  usePrevious,
  convertObjToArr,
} from '../../../../utils';

import ActionsAnnotations from '../../../../store/ducks/annotations';

import SectionCard from '../../../../components/SectionCard';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import AnnotationComment from '../../../../components/Comment';
import InputAnnotation from './InputAnnotation';
import Loading from './Loading';
import useStyles from './styles';

const Notes = props => {
  const { onSectionChange } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { current, list } = useSelector(state => state.course);
  const { list: allAnnotationsObj, allLoaded } = useSelector(
    state => state.annotations,
  );
  const [notesRequested, setNotesRequested] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [notesInfos, setNotesInfos] = useState({});
  const [dialogConfirm, setDialogConfirm] = useState(false);
  const [, setAnnotationRemoving] = useState(false);
  const [annotationEditing, setAnnotationEditing] = useState(null);
  const [, setAnnotationStoring] = useState(false);
  const [showNewInput, setShowNewInput] = useState(true);
  const [paginationShowAll, setPaginationShowAll] = useState(false);
  const [paginatedNotes, setPaginatedNotes] = useState([]);
  const prevCurrent = usePrevious(current);

  const checkLessonChange = useCallback(
    (course, lesson) =>
      prevCurrent &&
      (course !== prevCurrent.course || lesson !== prevCurrent.lesson),
    [prevCurrent],
  );

  useEffect(() => {
    if (current) {
      const { course, lesson } = current;
      if (prevCurrent && checkLessonChange(course, lesson)) {
        setNotesRequested(false);
      }
    }
  }, [checkLessonChange, current, prevCurrent]);

  useEffect(() => {
    if (!loading) {
      const lessonChange = checkLessonChange(current?.course, current?.lesson);
      if (lessonChange) setLoading(true);
    }
  }, [current, loading, checkLessonChange]);

  useEffect(() => {
    if (!current || !list[current.course]?.courseDetailed) return;
    const hasCourse = current?.course in list;
    const hasLesson = current?.lesson in list[current.course]?.lessons;
    if (hasCourse && hasLesson) {
      const { course, lesson } = current;
      const lessonInfos = list[course].lessons[lesson];

      if (!allLoaded && !lessonInfos?.annotationsLoaded && !notesRequested) {
        dispatch(ActionsAnnotations.requestLessonAnnotations(course, lesson));
        setNotesRequested(true);
      }
      if (allLoaded || lessonInfos?.annotationsLoaded) {
        const currentLessonNotes = convertObjToArr(allAnnotationsObj).filter(
          note => Number(note.lessonId) === Number(lesson),
        );
        setNotes(currentLessonNotes);
        setLoading(false);
      }
    }
  }, [current, list, notesRequested, allAnnotationsObj, allLoaded, dispatch]);

  useEffect(() => {
    setNotesInfos({
      notesShowing: notes.length,
      notesAmmount: notes.length,
    });
    setShowNewInput(true);
    setAnnotationRemoving(oldAnnotationRemoving => {
      if (oldAnnotationRemoving) {
        setDialogConfirm(false);
        enqueueSnackbar('Anotação removida com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldAnnotationRemoving;
    });
    setAnnotationEditing(oldAnnotationEditing => {
      if (oldAnnotationEditing) {
        enqueueSnackbar('Anotação alterada com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldAnnotationEditing;
    });
    setAnnotationStoring(oldAnnotationStoring => {
      if (oldAnnotationStoring) {
        enqueueSnackbar('Anotação adicionada com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldAnnotationStoring;
    });
  }, [enqueueSnackbar, notes]);

  useEffect(() => {
    setPaginatedNotes(
      paginateArr(
        sortArrByKey(notes, 'createdAt', true),
        paginationShowAll ? notes.length : 5,
        1,
      ),
    );
  }, [paginationShowAll, notes]);

  const handlePagination = useCallback(() => {
    setPaginationShowAll(oldState => !oldState);
  }, []);

  const handleStoreAnnotation = useCallback(() => {
    setAnnotationStoring(true);
  }, []);

  const handleEditAnnotation = useCallback(
    (annotationId = null) => ({
      openEdit: () => {
        setAnnotationEditing(annotationId);
        setShowNewInput(false);
      },
      cancelEdit: () => {
        setAnnotationEditing(null);
        setShowNewInput(true);
      },
    }),
    [],
  );

  const handleRemoveAnnotation = useCallback(
    annotationId => {
      const { course, lesson } = current;
      const dialogConfig = {
        type: 'warning',
        icon: 'warning',
        title: 'Tem certeza que deseja remover a anotação?',
        description: '',
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        isOpen: true,
        loading: false,
        confirmCb: () => {
          dispatch(
            ActionsAnnotations.requestRemoveAnnotation(
              course,
              lesson,
              annotationId,
            ),
          );
          setDialogConfirm({ ...dialogConfig, loading: true });
          setAnnotationRemoving(true);
        },
        cancelCb: () => {
          setDialogConfirm(false);
        },
      };
      setDialogConfirm(dialogConfig);
    },
    [current, dispatch],
  );

  const annotationActions = useCallback(
    annotationId => [
      {
        name: 'Editar',
        cb: () => handleEditAnnotation(annotationId).openEdit(),
      },
      {
        name: 'Remover',
        cb: () => handleRemoveAnnotation(annotationId),
      },
    ],
    [handleEditAnnotation, handleRemoveAnnotation],
  );

  const annotationEdit = useCallback(
    (annotationId, annotationText) =>
      annotationEditing === annotationId && {
        ComponentInput: InputAnnotation,
        componentProps: {
          onCancel: () => handleEditAnnotation().cancelEdit(),
          editNote: {
            id: annotationId,
            text: annotationText,
          },
        },
      },
    [annotationEditing, handleEditAnnotation],
  );

  return loading ? (
    <SectionCard className="container-section">
      <Loading />
    </SectionCard>
  ) : (
    <SectionCard className="container-section" id="notes" title="Anotações">
      <ReactResizeDetector
        handleHeight
        onResize={(width, height) => onSectionChange('height', height)}
      />
      <Box className={classes.container}>
        {showNewInput ? (
          <InputAnnotation notes={notes} onStore={handleStoreAnnotation} />
        ) : null}
        {notesInfos.notesShowing !== notesInfos.notesAmmount ? (
          <Typography className="notes-ammount" variant="caption" component="i">
            {`Mostrando ${notesInfos.notesShowing} de ${notesInfos.notesAmmount} anotações.`}
          </Typography>
        ) : null}
        {paginatedNotes.map(note => (
          <AnnotationComment
            key={note.id}
            title={moment(note.createdAt).format('DD/MM/YYYY - HH[h]mm[m]')}
            msg={note.description}
            type="note"
            actions={annotationActions(note.id)}
            editComment={annotationEdit(note.id, note.description)}
          />
        ))}
        {notes.length > 5 ? (
          <Link
            className="notes-load-more"
            underline="always"
            component="button"
            onClick={handlePagination}
          >
            {paginationShowAll ? 'Motrar menos' : 'Motrar tudo'}
          </Link>
        ) : null}
        {dialogConfirm ? <ConfirmationDialog {...dialogConfirm} /> : null}
      </Box>
    </SectionCard>
  );
};

export default memo(Notes);
